import { useMemo } from 'react';
import { parseFormData } from '../../../utils/formCrafter/formDataUtils';
import {
  getVisibleFieldCommentStatus,
  filterVisibleFieldComments,
} from '../../../utils/formCrafter/listViewUtils';
import { isFieldVisible } from '../../../utils/formCrafter/visibilityUtils';

/**
 * useVisibleComments Hook
 *
 * Filters comments to only include those on visible fields.
 * Used to prevent showing comment indicators for hidden fields
 * and to validate send-back actions.
 *
 * @param {Object} options - Hook options
 * @param {Array} options.comments - Array of all comments
 * @param {Array} options.subforms - Array of subform objects with json_object
 * @param {Object} options.filledForm - Filled form data object
 * @returns {Object} - Visibility-aware comment data
 */
const useVisibleComments = ({ comments = [], subforms = [], filledForm }) => {
  const formData = useMemo(() => {
    if (!filledForm) return {};
    return parseFormData(filledForm);
  }, [filledForm]);

  const commentStatus = useMemo(() => {
    if (!subforms?.length || !comments?.length) return {};
    return getVisibleFieldCommentStatus(
      comments,
      subforms,
      formData,
      isFieldVisible
    );
  }, [comments, subforms, formData]);

  const visibleComments = useMemo(() => {
    if (!subforms?.length || !comments?.length) return [];
    return filterVisibleFieldComments(
      comments,
      subforms,
      formData,
      isFieldVisible
    );
  }, [comments, subforms, formData]);

  const hasVisibleComments = visibleComments.length > 0;

  return {
    commentStatus,
    visibleComments,
    hasVisibleComments,
  };
};

export default useVisibleComments;
